import React, { useEffect, useState } from "react";
import { Icon } from "@iconify/react";
import { fetchData } from "../utils/apiHelpers";
import { deleteOne, getAll, update } from "../api";
import { getRolePrivilegesByRoleId } from "../api/role-privilegeAPI";
import {
  getPagePrivileges,
  getRolePagePrivilegesByRoleId,
  updateRolePagePrivileges,
} from "../api/page-privilegeAPI";
import {
  getModulePrivileges,
  updateModulePrivilege,
} from "../api/module-privilegeAPI";
import Select from "react-select";
import { useFormBuilder } from "../utils/useFormBuilder";
import {
  exportPrivileges,
  importPrivileges,
} from "../api/privilege-transferAPI";
import { useUserProfile } from "../masterLayout/MasterLayoutBody";

const SettingsPageLayer = () => {
  const userProfile = useUserProfile();
  const [activeTab, setActiveTab] = useState("role-privileges");

  // Data stores
  const [searchTerm, setSearchTerm] = useState("");
  const [, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [privileges, setPrivileges] = useState([]);
  const [pages, setPages] = useState([]);
  const [userRoles, setUserRoles] = useState([]);
  const [, setRolePrivileges] = useState([]);
  const [rolePageOptions, setRolePageOptions] = useState([]);
  const [modulePrivileges, setModulePrivileges] = useState([]);

  // UI selections
  const [selectedRole, setSelectedRole] = useState(null);
  const [selectedPageRole, setSelectedPageRole] = useState(null);
  const [rolePrivilegeOptions, setRolePrivilegeOptions] = useState([]);

  const [showExportModal, setShowExportModal] = useState(false);
  const [exportForm, setExportForm] = useState({
    name: "",
    roleId: null,
    description: "",
  });
  const [exportErrors, setExportErrors] = useState({});
  const [showRoleActionsMenu, setShowRoleActionsMenu] = useState(false);

  const [showImportModal, setShowImportModal] = useState(false);
  const [importErrors, setImportErrors] = useState({});
  const [importFileData, setImportFileData] = useState(null);
  const [importFileName, setImportFileName] = useState("");
  const [importForm, setImportForm] = useState({
    roleId: null,
    newRoleName: "",
    importApiPrivileges: 1,
    importPagePrivileges: 1,
    importModulePrivileges: 0,
  });

  useEffect(() => {
    fetchData(getAll, { apiName: "users" }, setUsers);
    fetchData(getAll, { apiName: "roles" }, setRoles);
    fetchData(getAll, { apiName: "privileges" }, setPrivileges);
    getPagePrivileges().then((data) => {
      const sortedPages = data.sort((a, b) => {
        const nameA = (a.description || a.page || "").toLowerCase();
        const nameB = (b.description || b.page || "").toLowerCase();
        return nameA.localeCompare(nameB);
      });
      setPages(sortedPages);
    });
    fetchData(getAll, { apiName: "user-roles" }, setUserRoles);
    fetchData(getAll, { apiName: "role-privileges" }, setRolePrivileges);
    getModulePrivileges().then(setModulePrivileges);
  }, []);

  // Build user-role mapping options when a user is selected
  /* useEffect(() => {
    if (selectedUser) {
      const assigned = userRoles
        .filter(ur => ur.userId === selectedUser.value)
        .map(ur => ur.roleId);
      setUserRoleOptions(
        roles.map(r => ({ value: r.id, label: r.name }))
          .map(opt => ({
            ...opt,
            isSelected: assigned.includes(opt.value)
          }))
      );
    } else {
      setUserRoleOptions([]);
    }
  }, [selectedUser, userRoles, roles]); */

  // Build role-privilege mapping options when a role is selected
  useEffect(() => {
    const fetchPrivilegesForRole = async () => {
      if (!selectedRole || !privileges.length) {
        setRolePrivilegeOptions([]);
        return;
      }

      try {
        const rolePrivileges = await getRolePrivilegesByRoleId(
          selectedRole.value,
        );
        const assignedPrivilegeIds = new Set(
          rolePrivileges.map((rp) => rp.privilegeId),
        );
        const options = privileges.map((p) => ({
          id: p.id,
          resource: p.resource,
          action: p.action,
          label: `${p.resource}:${p.action}`,
          checked: assignedPrivilegeIds.has(p.id),
        }));

        setRolePrivilegeOptions(options);
      } catch (err) {
        console.error("Failed to fetch role privileges", err);
      }
    };

    fetchPrivilegesForRole();
  }, [selectedRole, privileges]);

  // Build page privilege mapping when a role is selected in module tab
  useEffect(() => {
    const fetchPagesForRole = async () => {
      if (!selectedPageRole || !pages.length) {
        setRolePageOptions([]);
        return;
      }
      try {
        const rolePages = await getRolePagePrivilegesByRoleId(
          selectedPageRole.value,
        );
        const assignedIds = new Set(rolePages.map((rp) => rp.pagePrivilegeId));
        const options = pages.map((p) => ({
          id: p.id,
          page: p.page,
          description: p.description,
          checked: assignedIds.has(p.id),
        }));
        setRolePageOptions(options);
      } catch (err) {
        console.error("Failed to fetch role page privileges", err);
      }
    };
    fetchPagesForRole();
  }, [selectedPageRole, pages]);

  // Save role→privileges
  const saveRolePrivileges = async () => {
    const selectedIds = rolePrivilegeOptions
      .filter((opt) => opt.checked)
      .map((opt) => opt.id);
    await fetchData(update, {
      apiName: "role-privileges/bulk",
      id: selectedRole.value,
      data: { privilegeIds: selectedIds }, // controller is already reading body.roleIds
    });
    fetchData(getAll, { apiName: "role-privileges" }, setRolePrivileges);
  };

  const saveRolePagePrivileges = async () => {
    const selectedIds = rolePageOptions
      .filter((p) => p.checked)
      .map((p) => p.id);
    await updateRolePagePrivileges(selectedPageRole.value, selectedIds);
    // refetch if needed
  };

  const handleToggleModulePrivilege = async (priv) => {
    const newEnabled = !priv.enabled;
    setModulePrivileges((current) =>
      current.map((p) =>
        p.id === priv.id ? { ...p, enabled: newEnabled } : p,
      ),
    );
    try {
      await updateModulePrivilege(priv.id, { enabled: newEnabled });
    } catch (err) {
      console.error("Failed to update module privilege", err);
    }
  };

  const roleSelectOptions = roles.map((r) => ({ value: r.id, label: r.name }));
  const hasSelectedRole = Boolean(selectedRole?.value);
  const roleInUseByUser = hasSelectedRole
    ? userRoles.some(
      (userRole) => Number(userRole.roleId) === Number(selectedRole.value),
    )
    : false;

  useEffect(() => {
    if (!hasSelectedRole) setShowRoleActionsMenu(false);
  }, [hasSelectedRole]);

  const exportFields = [
    { field: "name", label: "Name", type: "text", size: 12, required: true },
    {
      field: "roleId",
      label: "Role",
      type: "select",
      size: 12,
      options: roleSelectOptions,
      required: true,
    },
    {
      field: "description",
      label: "Role Description (optional)",
      type: "text",
      size: 12,
      maxLength: 100,
    },
  ];
  const { renderField: renderExportField } = useFormBuilder(
    exportFields,
    exportForm,
    setExportForm,
    exportErrors,
  );

  const importFields = [
    {
      field: "roleId",
      label: "Role",
      type: "select",
      size: 12,
      options: roleSelectOptions,
      disabled: !!importForm.newRoleName?.trim(),
      required: !importForm.newRoleName?.trim(),
    },
    {
      field: "newRoleName",
      label: "New Role Name (optional)",
      type: "text",
      size: 12,
    },
    {
      field: "importApiPrivileges",
      label: "Import API Privileges",
      type: "switch",
      size: 12,
    },
    {
      field: "importPagePrivileges",
      label: "Import Page Privileges",
      type: "switch",
      size: 12,
    },
    {
      field: "importModulePrivileges",
      label: "Import Module Privileges",
      type: "switch",
      size: 12,
    },
  ];
  const { renderField: renderImportField } = useFormBuilder(
    importFields,
    importForm,
    setImportForm,
    importErrors,
  );

  const validateImportShape = (payload) => {
    const hasBaseFields =
      payload &&
      typeof payload === "object" &&
      typeof payload.name === "string" &&
      Array.isArray(payload.api_privilege) &&
      Array.isArray(payload.page_privilege) &&
      Array.isArray(payload.module_privilege);
    if (!hasBaseFields) return false;

    const apiValid = payload.api_privilege.every(
      (item) => item && Number.isFinite(Number(item.privilege_id)),
    );
    const pageValid = payload.page_privilege.every(
      (item) => item && Number.isFinite(Number(item.page_privilege_id)),
    );
    const moduleValid = payload.module_privilege.every(
      (item) =>
        item && typeof item === "object" && typeof item.module === "string",
    );
    return apiValid && pageValid && moduleValid;
  };

  const resetExportModal = () => {
    setShowExportModal(false);
    setExportErrors({});
  };

  const resetImportModal = () => {
    setShowImportModal(false);
    setImportErrors({});
    setImportFileData(null);
    setImportFileName("");
    setImportForm({
      roleId: selectedRole?.value || null,
      newRoleName: "",
      importApiPrivileges: 1,
      importPagePrivileges: 1,
      importModulePrivileges: 0,
    });
  };

  const handleOpenExport = () => {
    const selectedRoleData = roles.find(
      (role) => role.id === selectedRole?.value,
    );
    setExportForm({
      name: "",
      roleId: selectedRole?.value || null,
      description: selectedRoleData?.description || "",
    });
    setExportErrors({});
    setShowExportModal(true);
    setShowRoleActionsMenu(false);
  };

  const handleOpenImport = () => {
    setImportForm({
      roleId: selectedRole?.value || null,
      newRoleName: "",
      importApiPrivileges: 1,
      importPagePrivileges: 1,
      importModulePrivileges: 0,
    });
    setImportErrors({});
    setImportFileData(null);
    setImportFileName("");
    setShowImportModal(true);
    setShowRoleActionsMenu(false);
  };

  const handleExportSave = async () => {
    const nextErrors = {};
    if (!exportForm.name?.trim()) nextErrors.name = "Name is required";
    if (!exportForm.roleId) nextErrors.roleId = "Role is required";
    if (exportForm.description && exportForm.description.trim().length > 100) {
      nextErrors.description = "Role Description cannot exceed 100 characters";
    }
    setExportErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) return;

    const selectedRoleData = roles.find(
      (role) => role.id === Number(exportForm.roleId),
    );
    const nextDescription = exportForm.description?.trim() || "";
    if ((selectedRoleData?.description || "") !== nextDescription) {
      await update("roles", Number(exportForm.roleId), {
        description: nextDescription,
      });
      await fetchData(getAll, { apiName: "roles" }, setRoles);
    }

    const payload = await exportPrivileges({
      name: exportForm.name.trim(),
      roleId: Number(exportForm.roleId),
    });

    const json = JSON.stringify(payload, null, 2);
    const blob = new Blob([json], { type: "application/json" });
    const downloadUrl = window.URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = downloadUrl;
    anchor.download = `${exportForm.name.trim()}.json`;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    window.URL.revokeObjectURL(downloadUrl);
    resetExportModal();
  };

  const handleImportFileChange = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const lowerName = file.name.toLowerCase();
    if (!lowerName.endsWith(".json")) {
      window.alert("Invalid file type. Please select a .json file.");
      event.target.value = "";
      return;
    }

    try {
      const text = await file.text();
      const parsed = JSON.parse(text);
      if (!validateImportShape(parsed)) {
        window.alert(
          "The selected JSON file is not in the expected privileges format.",
        );
        event.target.value = "";
        return;
      }

      const roleMatch = roles.find(
        (role) =>
          role.name?.toLowerCase() ===
          String(parsed.roleName || "").toLowerCase(),
      );

      setImportFileName(file.name);
      setImportFileData(parsed);
      setImportForm((current) => ({
        ...current,
        roleId: roleMatch?.id || null,
        newRoleName: roleMatch ? "" : parsed.roleName || "",
      }));
    } catch {
      window.alert("The selected file is not valid JSON.");
      event.target.value = "";
    }
  };

  const handleImportSave = async () => {
    if (!importFileData) {
      window.alert("Please select a valid privileges JSON file.");
      return;
    }

    const nextErrors = {};
    if (!importForm.newRoleName?.trim() && !importForm.roleId) {
      nextErrors.roleId = "Select a role or enter a new role name";
    }
    setImportErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) return;

    if (!window.confirm("This may overwrite existing privileges. Continue?")) {
      return;
    }

    const normalizedNewRoleName = importForm.newRoleName?.trim()
      ? importForm.newRoleName.trim().toLowerCase().replace(/\s+/g, "")
      : null;

    const importResult = await importPrivileges({
      roleId: importForm.newRoleName?.trim() ? null : Number(importForm.roleId),
      roleName: importFileData.roleName,
      newRoleName: normalizedNewRoleName,
      roleDescription: importFileData.roleDescription || null,
      importApiPrivileges: Boolean(importForm.importApiPrivileges),
      importPagePrivileges: Boolean(importForm.importPagePrivileges),
      importModulePrivileges: Boolean(importForm.importModulePrivileges),
      api_privilege: importFileData.api_privilege,
      page_privilege: importFileData.page_privilege,
      module_privilege: importFileData.module_privilege,
      created_by: userProfile?.email,
    });

    const updatedRoles = await fetchData(getAll, { apiName: "roles" }, setRoles);
    await fetchData(getAll, { apiName: "user-roles" }, setUserRoles);

    if (importResult?.roleId) {
      const importedRole = (updatedRoles || []).find(
        (role) => Number(role.id) === Number(importResult.roleId),
      );
      setSelectedRole(
        importedRole
          ? { value: importedRole.id, label: importedRole.name }
          : null,
      );
    }

    if (importResult?.roleId || selectedRole?.value) {
      const roleIdToLoad = Number(importResult?.roleId || selectedRole.value);
      const rolePrivileges = await getRolePrivilegesByRoleId(roleIdToLoad);
      const assignedPrivilegeIds = new Set(
        rolePrivileges.map((rp) => rp.privilegeId),
      );
      setRolePrivilegeOptions((current) =>
        current.map((option) => ({
          ...option,
          checked: assignedPrivilegeIds.has(option.id),
        })),
      );
    }
    resetImportModal();
  };

  const handleDeleteRole = async () => {
    if (!selectedRole?.value || roleInUseByUser) return;

    if (
      !window.confirm(
        "This will permanently delete the role and all related privileges. Continue?",
      )
    ) {
      return;
    }

    await deleteOne("roles", selectedRole.value);
    await fetchData(getAll, { apiName: "roles" }, setRoles);
    await fetchData(getAll, { apiName: "user-roles" }, setUserRoles);

    setSelectedRole(null);
    setRolePrivilegeOptions([]);
    setShowRoleActionsMenu(false);
  };

  return (
    <div className="card h-100 p-24 radius-12 overflow-hidden">
      <div className="card-body p-0" style={{ minHeight: "600px" }}>
        {/* Tabs */}
        <ul className="nav nav-tabs mb-3">
          {[
            { key: "role-privileges", label: "API Privileges" },
            { key: "module-privileges", label: "Page Privileges" },
            { key: "screen-privileges", label: "Module Privileges" },
          ].map((tab) => (
            <li key={tab.key} className="nav-item">
              <button
                className={`nav-link ${activeTab === tab.key ? "active" : ""}`}
                onClick={() => setActiveTab(tab.key)}
              >
                {tab.label}
              </button>
            </li>
          ))}
        </ul>
        {/* Assign Privileges Tab */}
        {activeTab === "role-privileges" && (
          <div>
            <div className="row">
              <div className="col-md-6">
                <p>
                  <strong>Assign Privileges to Roles</strong>
                </p>
              </div>
              <div className="col-md-6 d-flex align-items-center justify-content-end">
                <button
                  className="btn btn-primary mt-2"
                  disabled={!hasSelectedRole}
                  onClick={saveRolePrivileges}
                >
                  Save Privileges
                </button>
                <div className="dropdown mt-2 ms-2">
                  <button
                    type="button"
                    className="btn btn-link text-decoration-none text-body"
                    data-bs-toggle="dropdown"
                    onClick={() =>
                      setShowRoleActionsMenu((current) => !current)
                    }
                    disabled={!hasSelectedRole}
                    aria-expanded={showRoleActionsMenu}
                  >
                    <Icon icon="mdi:dots-vertical" className="text-xl" />
                  </button>
                  <ul
                    className={`dropdown-menu dropdown-menu-end ${showRoleActionsMenu ? "show" : ""}`}
                  >
                    <li>
                      <button
                        type="button"
                        className="dropdown-item"
                        onClick={handleOpenImport}
                        disabled={!hasSelectedRole}
                      >
                        Import Privileges
                      </button>
                    </li>
                    <li>
                      <button
                        type="button"
                        className="dropdown-item"
                        onClick={handleOpenExport}
                        disabled={!hasSelectedRole}
                      >
                        Export Privileges
                      </button>
                    </li>
                    <li>
                      <hr className="dropdown-divider" />
                    </li>
                    <li>
                      <button
                        type="button"
                        className="dropdown-item text-danger"
                        onClick={handleDeleteRole}
                        disabled={!hasSelectedRole || roleInUseByUser}
                      >
                        Delete
                      </button>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
            <div className="row">
              <div className="col-md-12 pb-24">
                <label className="form-label">Select Role</label>
                <Select
                  options={roles.map((r) => ({ value: r.id, label: r.name }))}
                  value={selectedRole}
                  onChange={setSelectedRole}
                  isSearchable
                  isClearable
                />
              </div>
              <div className="col-md-6">
                <label className="form-label">Privileges</label>
                {/* Search Filter */}
                <input
                  type="text"
                  className="form-control mb-3"
                  placeholder="Search privileges..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
              <div className="col-md-12 pt-12">
                <div className="row">
                  {Object.entries(
                    rolePrivilegeOptions
                      .filter((opt) =>
                        opt.label
                          .toLowerCase()
                          .includes(searchTerm.toLowerCase()),
                      )
                      .reduce((acc, opt) => {
                        if (!acc[opt.resource]) acc[opt.resource] = [];
                        acc[opt.resource].push(opt); // use original object
                        return acc;
                      }, {}),
                  ).map(([resource, actions]) => (
                    <div key={resource} className="col-md-4 mb-8">
                      <p className="fw-bold text-capitalize mb-8">{resource}</p>
                      {actions.map((opt) => (
                        <div
                          className="orm-switch switch-primary d-flex align-items-center gap-3"
                          key={opt.id}
                        >
                          <input
                            className="form-check-input"
                            type="checkbox"
                            id={`priv-${opt.id}`}
                            checked={opt.checked}
                            onChange={() => {
                              setRolePrivilegeOptions((current) =>
                                current.map((o) =>
                                  o.id === opt.id
                                    ? { ...o, checked: !o.checked }
                                    : o,
                                ),
                              );
                            }}
                            disabled={!hasSelectedRole}
                          />
                          <label
                            className="form-check-label"
                            htmlFor={`priv-${opt.id}`}
                          >
                            {opt.action}
                          </label>
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === "module-privileges" && (
          <div>
            <div className="row">
              <div className="col-md-6">
                <p>
                  <strong>Assign Page Access to Roles</strong>
                </p>
              </div>
              <div className="col-md-6 d-flex align-items-center justify-content-end">
                <button
                  className="btn btn-primary mt-2"
                  disabled={!selectedPageRole}
                  onClick={saveRolePagePrivileges}
                >
                  Save Pages
                </button>
              </div>
            </div>
            <div className="row">
              <div className="col-md-12 pb-24">
                <label className="form-label">Select Role</label>
                <Select
                  options={roles.map((r) => ({ value: r.id, label: r.name }))}
                  value={selectedPageRole}
                  onChange={setSelectedPageRole}
                  isSearchable
                  isClearable
                />
              </div>
              <div className="col-md-12 pt-12">
                <div className="row">
                  <p className="fw-bold text-capitalize mb-8">Pages</p>
                  {rolePageOptions.map((opt) => (
                    <div className="col-md-4 mb-8">
                      <div
                        className="orm-switch switch-primary d-flex align-items-center gap-3"
                        key={opt.id}
                      >
                        <input
                          className="form-check-input"
                          type="checkbox"
                          id={`page-${opt.id}`}
                          checked={opt.checked}
                          onChange={() => {
                            setRolePageOptions((current) =>
                              current.map((o) =>
                                o.id === opt.id
                                  ? { ...o, checked: !o.checked }
                                  : o,
                              ),
                            );
                          }}
                          disabled={!selectedPageRole}
                        />
                        <label
                          className="form-check-label"
                          htmlFor={`page-${opt.id}`}
                        >
                          {opt.description || opt.page}
                        </label>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === "screen-privileges" && (
          <div className="row">
            <p className="fw-bold text-capitalize mb-24 pt-8">
              Module Privileges
            </p>
            {modulePrivileges.map((priv) => (
              <div className="col-md-12 mb-8" key={priv.id}>
                <div className="orm-switch switch-primary d-flex align-items-center gap-3">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id={`module-priv-${priv.id}`}
                    checked={!!priv.enabled}
                    onChange={() => handleToggleModulePrivilege(priv)}
                  />
                  <label
                    className="form-check-label"
                    htmlFor={`module-priv-${priv.id}`}
                  >
                    {priv.description}
                  </label>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {showExportModal && (
        <div className="modal show d-block" tabIndex="-1">
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Export Privileges</h5>
                <button className="btn-close" onClick={resetExportModal} />
              </div>
              <div className="modal-body">
                <p>
                  This will export the API privileges, Page Privileges and
                  Module Privileges to a JSON file that can be loaded into
                  another database
                </p>
                <div className="row g-3">
                  {exportFields.map(renderExportField)}
                </div>
              </div>
              <div className="modal-footer">
                <button
                  className="btn btn-secondary"
                  onClick={resetExportModal}
                >
                  Cancel
                </button>
                <button className="btn btn-primary" onClick={handleExportSave}>
                  Save
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showImportModal && (
        <div className="modal show d-block" tabIndex="-1">
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Import Privileges</h5>
                <button className="btn-close" onClick={resetImportModal} />
              </div>
              <div className="modal-body">
                <div className="row g-3">
                  <div className="col-md-12">
                    <label className="form-label">Privileges JSON file</label>
                    <input
                      type="file"
                      className="form-control"
                      accept=".json,.JSON"
                      onChange={handleImportFileChange}
                    />
                    {importFileName && (
                      <small className="text-muted">
                        Loaded file: {importFileName}
                      </small>
                    )}
                  </div>
                  {importFileData && importFields.map(renderImportField)}
                </div>
              </div>
              <div className="modal-footer">
                <button
                  className="btn btn-secondary"
                  onClick={resetImportModal}
                >
                  Cancel
                </button>
                <button className="btn btn-primary" onClick={handleImportSave}>
                  Save
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SettingsPageLayer;
